import { authenticatedImageFetch, authenticatedFetch } from "../utils/utils";

/**
 * Récupère une image de profil et la convertit en URL blob
 * @param {string} mediaPath - Le chemin du média (ex: "community_profiles/1/profile_picture.png")
 * @returns {Promise<string>} - URL blob de l'image
 */
export async function getProfileMedia(mediaPath) {
    try {
        if (!mediaPath) {
            throw new Error("Chemin du média manquant");
        }

        // Nettoyer et encoder le chemin si nécessaire
        let encodedPath;
        if (mediaPath.includes('%')) {
            // Le chemin est déjà encodé
            encodedPath = mediaPath;
        } else {
            // Nettoyer le chemin et encoder seulement les caractères nécessaires
            const cleanPath = mediaPath.replace(/^\/+|\/+$/g, ''); // Enlever les slashes au début/fin
            const pathParts = cleanPath.split('/').map(part => encodeURIComponent(part));
            encodedPath = pathParts.join('/');
        }

        const response = await authenticatedImageFetch(encodedPath, {
            method: "GET"
        });

        if (!response.ok) {
            throw new Error(`Erreur HTTP: ${response.status} ${response.statusText}`);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        return url;
    } catch (error) {
        console.error("Erreur lors de la récupération du média de profil:", error);
        throw error;
    }
}

/**
 * Upload une photo de profil communautaire
 * @param {File} file - Le fichier image à uploader
 * @param {number} profileId - L'ID du profil communautaire
 * @returns {Promise<string>} - Le nouveau chemin de l'image
 */
export async function uploadProfilePicture(file, profileId) {
    try {
        if (!file) {
            throw new Error("Aucun fichier sélectionné");
        }

        if (!profileId) {
            throw new Error("ID du profil manquant");
        }

        // Vérifier le type de fichier
        if (!file.type.startsWith('image/')) {
            throw new Error("Le fichier doit être une image");
        }

        // Vérifier la taille du fichier (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            throw new Error("Le fichier est trop volumineux (max 5MB)");
        }

        const formData = new FormData();
        formData.append('profile_id', profileId);
        formData.append('profile_picture', file);

        const response = await authenticatedFetch('/api/v1/community-profile/', {
            method: 'POST',
            body: formData,
            headers: {
                // Ne pas définir Content-Type, laissez le navigateur le faire automatiquement pour FormData
            }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Erreur HTTP: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();

        // Retourner le chemin de l'image depuis la réponse
        if (result.profile_picture) {
            return result.profile_picture;
        } else {
            throw new Error("Aucun chemin d'image retourné par le serveur");
        }
    } catch (error) {
        console.error("Erreur lors de l'upload de la photo de profil:", error);
        throw error;
    }
}

/**
 * Nettoie les URLs blob pour éviter les fuites mémoire
 * @param {string} blobUrl - L'URL blob à nettoyer
 */
export function revokeProfileMediaUrl(blobUrl) {
    if (blobUrl && blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrl);
    }
} 